import React from 'react';
import { useSiteConfig } from '../hooks/useSiteConfig';
import { useTestimonials } from '../hooks/useTestimonials';
import Editable from '../components/Editable';
import './Home.css';

const DEFAULT_HERO = {
  title: 'Thoughtfully Handmade, Beautifully Designed',
  subtitle: "Find meaningful, one-of-a-kind pieces perfect for gifting or adding a personal touch to your home.",
  cta_primary: { label: 'Explore Handmade Goods', href: '/portfolio' },
  cta_secondary: { label: 'Shop The Collection', href: '/services' },
  image_url: '',
};

const DEFAULT_HOME_FEATURES = [
  { title: 'Hand Crafted', description: 'Every piece is made by hand with care and attention to detail.' },
  { title: 'Collaborative Design', description: 'We work closely with you to bring your vision to life.' },
  { title: 'Functional Beauty', description: 'Our pieces are crafted to be as functional as they are beautiful — made to be used and enjoyed every day.' },
];

const DEFAULT_HOME_CLOSING = {
  title: "Let's work together to achieve your goals.",
  cta_label: 'Contact Us Today',
  cta_href: '/contact',
};

export default function Home() {
  const { config } = useSiteConfig();
  const { items: testimonials } = useTestimonials();

  const hero = config.hero || DEFAULT_HERO;
  const features = Array.isArray(config.home_features) && config.home_features.length
    ? config.home_features
    : DEFAULT_HOME_FEATURES;
  const closing = config.home_closing || DEFAULT_HOME_CLOSING;
  const heroImage = (hero && hero.image_url) || `${process.env.PUBLIC_URL}/hero-banner.png`;
  const heroPrimary = (hero && hero.cta_primary) || DEFAULT_HERO.cta_primary;
  const heroSecondary = (hero && hero.cta_secondary) || DEFAULT_HERO.cta_secondary;

  return (
    <main className="home">
      {/* Hero Section */}
      <Editable descriptor={{ kind: 'config', key: 'hero', label: 'Home page banner' }}>
      <section
        className="hero"
        style={{
          backgroundImage: `linear-gradient(135deg, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.3) 100%), url(${heroImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="hero-content">
          <h1>{hero.title || DEFAULT_HERO.title}</h1>
          <p>{hero.subtitle || DEFAULT_HERO.subtitle}</p>
          <div className="hero-buttons">
            {heroPrimary && heroPrimary.label && (
              <a href={heroPrimary.href || '/'} className="btn btn-primary">{heroPrimary.label}</a>
            )}
            {heroSecondary && heroSecondary.label && (
              <a href={heroSecondary.href || '/'} className="btn btn-secondary">{heroSecondary.label}</a>
            )}
          </div>
        </div>
      </section>
      </Editable>

      {/* Features Section */}
      <Editable descriptor={{ kind: 'config', key: 'home_features', label: '"Why Choose Us" boxes' }}>
      <section className="features">
        <div className="container">
          <h2>Why Choose Us?</h2>
          <div className="features-grid">
            {features.map((f, i) => (
              <div className="feature-card" key={i}>
                <h3>{f.title}</h3>
                <p>{f.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      </Editable>

      {/* Testimonials */}
      {testimonials && testimonials.length > 0 && (
        <Editable descriptor={{ kind: 'testimonials', label: 'Customer reviews' }}>
        <section className="features testimonials-section">
          <div className="container">
            <h2>What Our Customers Say</h2>
            <div className="features-grid">
              {testimonials.map((t) => (
                <div className="feature-card testimonial-card" key={t.id}>
                  <div className="testimonial-rating" aria-label={`Rated ${t.rating} out of 5`}>
                    {'★'.repeat(Math.max(1, Math.min(5, t.rating || 5)))}
                    <span className="testimonial-rating-dim">
                      {'★'.repeat(5 - Math.max(1, Math.min(5, t.rating || 5)))}
                    </span>
                  </div>
                  <p className="testimonial-body">"{t.body}"</p>
                  <p className="testimonial-attrib">
                    <strong>{t.name}</strong>
                    {t.role ? <span> — {t.role}</span> : null}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>
        </Editable>
      )}

      {/* CTA Section */}
      <Editable descriptor={{ kind: 'config', key: 'home_closing', label: 'Invitation at the bottom' }}>
      <section className="cta">
        <div className="container">
          <h2>{closing.title || DEFAULT_HOME_CLOSING.title}</h2>
          <a href={closing.cta_href || '/contact'} className="btn btn-primary">
            {closing.cta_label || DEFAULT_HOME_CLOSING.cta_label}
          </a>
        </div>
      </section>
      </Editable>
    </main>
  );
}
